/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { DroppedServiceRequestError } from '../error';

export class TiVoNetworkRequestModel<R> {
    private static mNetworkCallId: number = 0;

    private static mProcessingMap: Map<string, number[]> = new Map<string, number[]>();

    public networkCallId: number;

    private mServiceApi: CallableFunction;

    private mRequestParams: any[];

    public constructor(serviceApi: CallableFunction) {
        TiVoNetworkRequestModel.mNetworkCallId += 1;
        this.networkCallId = TiVoNetworkRequestModel.mNetworkCallId;
        this.mServiceApi = serviceApi;
        this.mRequestParams = [];
        const calls: number[] =
            TiVoNetworkRequestModel.mProcessingMap.get(this.mServiceApi.name) || [];
        calls.push(this.networkCallId);
        TiVoNetworkRequestModel.mProcessingMap.set(this.mServiceApi.name, calls);
    }

    /**
     * The method will drop all in progress service calls for the service API.
     * @param serviceApi (CallableFunction) - callable function to drop call. Default to current
     * instance service API.
     */
    public drop(serviceApi: CallableFunction = this.mServiceApi): void {
        TiVoNetworkRequestModel.mProcessingMap.set(serviceApi.name, [this.networkCallId]);
    }

    /**
     * Method to add request parameters in comma separated manner.
     * @param params (any[]) - list of comma separated parameters.
     */
    // TODO: We need to move this method to chaining mechanism.
    public addRequestParams(...params: any[]): void {
        this.mRequestParams = params;
    }

    /**
     * The method will take the required service API parameters and make the request.
     * @param ...options (any[]) - list of service arguments.
     * @returns `Promise<R>`: A promise of data with attached networkCallId.
     * @throws `ServiceApiError | DroppedServiceRequestError`
     */
    public async send(): Promise<R> {
        try {
            const response = await this.mServiceApi(...this.mRequestParams);
            const calls: number[] =
                TiVoNetworkRequestModel.mProcessingMap.get(this.mServiceApi.name) || [];
            const index = calls.indexOf(this.networkCallId);
            if (index === -1) {
                throw new DroppedServiceRequestError();
            }
            calls.splice(index, 1);
            TiVoNetworkRequestModel.mProcessingMap.set(this.mServiceApi.name, calls);
            return <R>{ networkCallId: this.networkCallId, ...response };
        } catch (error: any) {
            error.networkCallId = this.networkCallId;
            throw error;
        }
    }

    /**
     * The method will reset the `TiVoNetworkRequestModel`. This will result in dropping all the
     * in progress calls.
     */
    public static reset() {
        TiVoNetworkRequestModel.mProcessingMap = new Map<string, number[]>();
    }
}
